const MAX_ORDERS = 20;
const MAX_STEPS_PER_ORDER = 50;
const STORAGE_PREFIX = 'eportal:orderHistory:';

function decodeJwtUserKey(token) {
  if (!token || typeof token !== 'string') return 'anon';
  try {
    const part = token.split('.')[1];
    if (!part) return 'anon';
    const padded = part.replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(padded + '==='.slice((padded.length + 3) % 4));
    const payload = JSON.parse(decodeURIComponent(escape(json)));
    return payload.email || payload.preferred_username || payload.upn || payload.sub || 'anon';
  } catch (e) {
    return 'anon';
  }
}

function clone(value) {
  return JSON.parse(JSON.stringify(value));
}

function equals(a, b) {
  return JSON.stringify(a) === JSON.stringify(b);
}

class OrderHistoryStore {
  constructor() {
    this.userKey = null;
    this.storageKey = null;
    this.persistDisabled = false;
    this.state = { orders: {} };
  }

  init(token) {
    const userKey = decodeJwtUserKey(token);
    if (this.userKey === userKey && this.storageKey) return;

    this.userKey = userKey;
    this.storageKey = STORAGE_PREFIX + userKey;
    this.persistDisabled = false;
    this._loadFromStorage();
  }

  _loadFromStorage() {
    try {
      const raw = localStorage.getItem(this.storageKey);
      this.state = raw ? JSON.parse(raw) : { orders: {} };
      if (!this.state.orders || typeof this.state.orders !== 'object') {
        this.state = { orders: {} };
      }
    } catch (e) {
      console.warn('OrderHistoryStore: kon state niet laden uit localStorage', e);
      this.state = { orders: {} };
    }
  }

  _flush() {
    if (this.persistDisabled || !this.storageKey) return;
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.state));
    } catch (e) {
      if (e && (e.name === 'QuotaExceededError' || e.code === 22)) {
        this._evictOldest(5);
        try {
          localStorage.setItem(this.storageKey, JSON.stringify(this.state));
        } catch (e2) {
          console.warn('OrderHistoryStore: localStorage vol — persist uitgeschakeld voor deze sessie', e2);
          this.persistDisabled = true;
        }
      } else {
        console.warn('OrderHistoryStore: kon state niet opslaan', e);
        this.persistDisabled = true;
      }
    }
  }

  _evictLRU() {
    const ids = Object.keys(this.state.orders);
    if (ids.length <= MAX_ORDERS) return;
    ids.sort((a, b) => {
      const ta = this.state.orders[a].lastAccessed || '';
      const tb = this.state.orders[b].lastAccessed || '';
      return ta < tb ? -1 : ta > tb ? 1 : 0;
    });
    const toRemove = ids.length - MAX_ORDERS;
    for (let i = 0; i < toRemove; i++) {
      delete this.state.orders[ids[i]];
    }
  }

  _evictOldest(n) {
    const ids = Object.keys(this.state.orders);
    ids.sort((a, b) => {
      const ta = this.state.orders[a].lastAccessed || '';
      const tb = this.state.orders[b].lastAccessed || '';
      return ta < tb ? -1 : ta > tb ? 1 : 0;
    });
    for (let i = 0; i < Math.min(n, ids.length); i++) {
      delete this.state.orders[ids[i]];
    }
  }

  _entry(orderId) {
    return this.state.orders[String(orderId)];
  }

  // Returns object: { snapshot, serverChanged } where serverChanged = true if local stack was discarded
  load(orderId, serverOutput) {
    if (!orderId) return { snapshot: serverOutput, serverChanged: false };
    const key = String(orderId);
    const baseline = clone(serverOutput || {});
    let entry = this.state.orders[key];

    if (!entry) {
      entry = {
        stack: [baseline],
        cursor: 0,
        baseline,
        lastAccessed: new Date().toISOString()
      };
      this.state.orders[key] = entry;
      this._evictLRU();
      this._flush();
      return { snapshot: baseline, serverChanged: false };
    }

    if (!equals(entry.baseline, baseline)) {
      // Server-state has moved on — drop local stack
      entry.stack = [baseline];
      entry.cursor = 0;
      entry.baseline = baseline;
      entry.lastAccessed = new Date().toISOString();
      this._flush();
      return { snapshot: baseline, serverChanged: true };
    }

    entry.lastAccessed = new Date().toISOString();
    this._flush();
    const current = entry.stack[entry.cursor];
    return { snapshot: clone(current), serverChanged: false };
  }

  commit(orderId, snapshot) {
    if (!orderId) return;
    const key = String(orderId);
    const entry = this._entry(orderId);
    if (!entry) {
      this.load(orderId, snapshot);
      return;
    }
    const snap = clone(snapshot);
    if (equals(entry.stack[entry.cursor], snap)) {
      // No-op: identical to current cursor
      return;
    }
    // Truncate redo-tail
    entry.stack = entry.stack.slice(0, entry.cursor + 1);
    entry.stack.push(snap);
    // Cap stack size
    if (entry.stack.length > MAX_STEPS_PER_ORDER) {
      const overflow = entry.stack.length - MAX_STEPS_PER_ORDER;
      entry.stack.splice(0, overflow);
    }
    entry.cursor = entry.stack.length - 1;
    entry.lastAccessed = new Date().toISOString();
    this.state.orders[key] = entry;
    this._evictLRU();
    this._flush();
  }

  undo(orderId) {
    const entry = this._entry(orderId);
    if (!entry || entry.cursor <= 0) return null;
    entry.cursor -= 1;
    entry.lastAccessed = new Date().toISOString();
    this._flush();
    return clone(entry.stack[entry.cursor]);
  }

  redo(orderId) {
    const entry = this._entry(orderId);
    if (!entry || entry.cursor >= entry.stack.length - 1) return null;
    entry.cursor += 1;
    entry.lastAccessed = new Date().toISOString();
    this._flush();
    return clone(entry.stack[entry.cursor]);
  }

  canUndo(orderId) {
    const entry = this._entry(orderId);
    return !!(entry && entry.cursor > 0);
  }

  canRedo(orderId) {
    const entry = this._entry(orderId);
    return !!(entry && entry.cursor < entry.stack.length - 1);
  }

  markSaved(orderId, snapshot) {
    const entry = this._entry(orderId);
    if (!entry) return;
    entry.baseline = clone(snapshot);
    entry.lastAccessed = new Date().toISOString();
    this._flush();
  }

  clear(orderId) {
    if (!orderId) return;
    delete this.state.orders[String(orderId)];
    this._flush();
  }
}

const orderHistoryStore = new OrderHistoryStore();
export default orderHistoryStore;
